// De diensten waarmee een bewoner kan meedoen na een 'ja' aan de deur — fixer,
// energiecoach, cv-tuner, een eigen partner — per organisatie in te richten
// (Instellingen → Ondersteuning → 'Diensten'). Sinds 2026-10-13 dragen ook
// huishoudens ze (`Adresses.Diensten`), met dezelfde sleutels.
//
// Nodig omdat de lijst per organisatie verschilt: Zutphen Energie heeft
// 'partner ZE', een ander klusbedrijf een andere partner (besluit Gijs
// 2026-10-12). Zelfde opslag als de labels bij huishoudens
// (resolveAdressLabels): `CompagnyMeta.WervingsDiensten` is een map per
// sleutel `{ Label, Position, Hidden }`, te schrijven met een dotted path.
// Een registratierij draagt de SLEUTELS in `Diensten: string[]`; hernoemen
// raakt dus geen rij, en een dienst wordt verborgen, nooit verwijderd.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (Uitbreiding 2026-10 (2))

import { v4 as uuid } from 'uuid'

/**
 * @typedef {object} WervingsDienst
 * @property {string} key - De sleutel zoals hij op een registratierij staat
 * @property {string} label - De naam die de gebruiker leest
 * @property {boolean} [hidden] - Alleen met `includeHidden`
 */

/**
 * Aan/uit-vlaggen komen in twee vormen voor: ToggleButton schrijft tekst.
 * @param {*} value
 * @returns {boolean}
 */
const aan = (value) => value === true || value === 'true'

/** De naam van een sleutel die de organisatie niet (meer) kent. */
const ONBEKENDE_DIENST = 'Onbekende dienst'

/**
 * Alle diensten, ook die zonder naam: wie de naam leegmaakt om hem opnieuw te
 * typen, mag de regel in het instellingenscherm niet kwijtraken (de autosave
 * schrijft de lege naam weg vóór de nieuwe). Alleen het instellingenscherm
 * krijgt een naamloze dienst te zien; overal elders telt hij niet als keuze.
 *
 * @param {object} [meta] - Het CompagnyMeta-document, of `{ WervingsDiensten }`
 * @returns {Array<{ key: string, label: string, position: number, hidden: boolean }>}
 */
const alleDiensten = (meta) => {
    const config = meta?.WervingsDiensten
    if (!config || typeof config !== 'object' || Array.isArray(config)) return []

    return Object.keys(config)
        .map((key) => ({ key, ...config[key] }))
        .filter((d) => d && typeof d === 'object')
        .map((d) => ({
            key: d.key,
            label: typeof d.Label === 'string' ? d.Label.trim() : '',
            position: Number(d.Position) || 0,
            hidden: aan(d.Hidden),
        }))
        .sort((a, b) => a.position - b.position || a.key.localeCompare(b.key))
}

/**
 * De diensten van een organisatie, op volgorde.
 *
 * @param {object} [meta] - Het CompagnyMeta-document, of `{ WervingsDiensten }`
 * @param {object} [opties]
 * @param {boolean} [opties.includeHidden=false] - Ook de verborgen, met `hidden` (instellingenscherm)
 * @returns {Array<WervingsDienst>}
 */
const resolveWervingsDiensten = (meta, { includeHidden = false } = {}) => alleDiensten(meta)
    .filter((d) => includeHidden || (!d.hidden && d.label !== ''))
    .map(({ key, label, hidden }) => (includeHidden ? { key, label, hidden } : { key, label }))

/**
 * Sleutel → naam, óók van verborgen diensten: een rij die een verborgen dienst
 * draagt moet hem nog kunnen tonen en tellen.
 *
 * @param {object} [meta]
 * @returns {Object<string, string>}
 */
const dienstNamen = (meta) => Object.fromEntries(alleDiensten(meta).filter((d) => d.label !== '').map((d) => [d.key, d.label]))

/**
 * De diensten om te tonen: de zichtbare, plus elke sleutel die in de gegevens
 * voorkomt maar niet (meer) zichtbaar is — een verborgen dienst blijft op de
 * rijen die hem dragen leesbaar en uit te zetten. Eén recept voor het
 * registratievenster en het overzicht.
 *
 * @param {Array<{ key: string, label: string }>} zichtbaar - Uit resolveWervingsDiensten
 * @param {Array<string>} gebruikt - Sleutels die in de gegevens voorkomen
 * @param {Object<string, string>} namen - Uit dienstNamen
 * @returns {Array<{ key: string, label: string }>}
 */
const dienstKeuzes = (zichtbaar, gebruikt, namen) => [
    ...zichtbaar,
    ...[...new Set(gebruikt || [])]
        .filter((key) => !zichtbaar.some((d) => d.key === key))
        .map((key) => ({ key, label: namen?.[key] || ONBEKENDE_DIENST })),
]

/**
 * @param {{ Diensten?: * }} [rij] - Een Wervingsregistratie
 * @returns {Array<string>} De dienstsleutels; een ontbrekend of verkeerd veld leest als geen
 */
const dienstenOf = (rij) => (Array.isArray(rij?.Diensten) ? rij.Diensten.filter((k) => typeof k === 'string') : [])

/** @returns {string} Een nieuwe sleutel voor een dienst */
const newDienstKey = () => `dienst-${uuid()}`

export { ONBEKENDE_DIENST, dienstKeuzes, dienstNamen, dienstenOf, newDienstKey }
export default resolveWervingsDiensten
